import { useCallback, useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { SPORTS, fmtDate } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const INGREDIENTS = ["positions", "position_group", "tags", "stats", "class_years", "min_height", "max_height", "gaps"];
const pickIngredients = (r) => Object.fromEntries(INGREDIENTS.filter((k) => r?.[k] != null && !(Array.isArray(r[k]) && !r[k].length)).map((k) => [k, r[k]]));
const inputCls = "h-9 w-full rounded-md border border-slate-800 bg-[#0B0F19] px-3 text-sm outline-none focus:border-[#FF2D78]";

const ProtoRow = ({ p, onSaved }) => {
  const [f, setF] = useState({ name: p.name, description: p.description || "", recipe: JSON.stringify(pickIngredients(p.recipe), null, 1) });
  const save = async (extra = {}) => {
    let recipe;
    try { recipe = JSON.parse(f.recipe); } catch { toast.error("Ingredients must be valid JSON"); return; }
    try {
      await api.put(`/admin/prototypes/${p.slug}`, { name: f.name, description: f.description, recipe, ...extra });
      toast.success(`${f.name} saved`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid={`admin-prototype-${p.slug}`} className="grid gap-3 border-b border-slate-800 p-4 lg:grid-cols-[1fr_1.4fr_auto]">
      <div className="space-y-2">
        <input data-testid={`admin-prototype-name-${p.slug}`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
        <input data-testid={`admin-prototype-desc-${p.slug}`} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} placeholder="One-line description" />
      </div>
      <textarea data-testid={`admin-prototype-recipe-${p.slug}`} value={f.recipe} onChange={(e) => setF({ ...f, recipe: e.target.value })} rows={4}
        className="w-full rounded-md border border-slate-800 bg-[#0B0F19] p-2 font-data text-[11px] text-slate-300 outline-none focus:border-[#FF2D78]" />
      <div className="flex items-start gap-3">
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <Switch data-testid={`admin-prototype-enabled-${p.slug}`} checked={p.enabled} onCheckedChange={(v) => save({ enabled: v })} /> {p.enabled ? "On" : "Off"}
        </label>
        <button data-testid={`admin-prototype-save-${p.slug}`} onClick={() => save()} className="rounded-md p-2 text-slate-300 hover:bg-slate-800"><Save className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const PrototypesTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/prototypes").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-sm text-slate-500">Loading prototypes</p>;
  const sports = [...new Set(data.items.map((p) => p.sport))];
  return (
    <div className="grid gap-8 xl:grid-cols-[1fr_340px]">
      <div className="space-y-6">
        <p className="max-w-2xl text-sm text-slate-400">Ready-made player types members can pick on the Prototypes page. Ingredients use the same keys the finder understands; unknown keys are dropped when saving.</p>
        {sports.map((s) => (
          <div key={s} className="overflow-hidden rounded-xl border border-slate-800">
            <div className="bg-[#131927] px-4 py-3 font-display text-lg font-extrabold uppercase" style={{ color: SPORTS[s]?.accent }}>{SPORTS[s]?.label}</div>
            {data.items.filter((p) => p.sport === s).map((p) => <ProtoRow key={`${p.slug}-${p.enabled}`} p={p} onSaved={load} />)}
          </div>
        ))}
      </div>
      <div>
        <h3 className="mb-3 font-display text-xl font-extrabold uppercase">Common typed requests</h3>
        <div data-testid="admin-prototype-queries" className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {data.queries.map((q) => (
            <div key={q.text} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
              <div className="min-w-0"><div className="text-slate-200">{q.text}</div>
                <div className="font-data text-[10px] text-slate-500">{SPORTS[q.sport]?.label || "Sport unclear"} · {fmtDate(q.last)}</div></div>
              <span className="font-data text-xs text-[#FF2D78]">×{q.count}</span>
            </div>
          ))}
          {!data.queries.length && <p className="p-4 text-sm text-slate-500">No typed requests yet.</p>}
        </div>
      </div>
    </div>
  );
};
